import React, { useState, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, DatePicker, Space,
  Typography, Alert, Radio, Spin, Empty, Button,
} from 'antd';
import { TeamOutlined, WarningOutlined, DownloadOutlined } from '@ant-design/icons';
import { buildLeaderboardCsv } from '../utils/engineerPerformanceCsv';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DashboardLayout } from '../components/layout';
import { getAgentAuthHeaders } from '../services/api/settingsApi';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const AGENT_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

const PRESETS = [
  { key: '1m', label: '1m', months: 1 },
  { key: '3m', label: '3m', months: 3 },
  { key: '6m', label: '6m', months: 6 },
  { key: '12m', label: '12m', months: 12 },
];

async function fetchLeaderboard({ from, to, queue }) {
  const params = new URLSearchParams({ from, to });
  if (queue) params.set('queue', queue);
  const resp = await fetch(`${AGENT_URL}/engineer-performance/leaderboard?${params}`, {
    headers: getAgentAuthHeaders(),
  });
  if (!resp.ok) throw new Error(`Leaderboard request failed (${resp.status})`);
  return resp.json();
}

const fmtHours = (v) => (v == null ? '—' : `${v.toFixed(1)}h`);
const fmtMins = (v) => (v == null ? '—' : `${Math.round(v)}m`);
const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(0)}%`);
const fmtCsat = (v) => (v == null ? '—' : `${v.toFixed(1)} / 3`);

function EngineerDetail({ resourceId, from, to, team }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['engineer-performance-detail', resourceId, from, to],
    queryFn: async () => {
      const resp = await fetch(
        `${AGENT_URL}/engineer-performance/engineer/${resourceId}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        { headers: getAgentAuthHeaders() });
      if (!resp.ok) throw new Error(`Detail request failed (${resp.status})`);
      return resp.json();
    },
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  if (isPending) return <div style={{ padding: 16 }}><Spin /> Loading detail…</div>;
  if (isError) return <Alert type="error" message="Could not load engineer detail" showIcon />;

  return (
    <div style={{ background: 'var(--odin-surface)', padding: 16, borderRadius: 8 }}>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Text strong>Tickets by category</Text>
          {data.category_breakdown.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(120, data.category_breakdown.length * 34)}>
              <BarChart data={data.category_breakdown} layout="vertical"
                        margin={{ left: 24, right: 24, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="category" width={160} />
                <RTooltip />
                <Bar dataKey="count" fill="var(--odin-accent)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Col>
        <Col xs={24} md={12}>
          <Text strong><WarningOutlined /> Struggle signals — {data.flagged_tickets.length} flagged (signals, not verdicts)</Text>
          {data.flagged_tickets.length === 0 ? <Empty description="No flagged tickets" /> : (
            <div style={{ marginTop: 8 }}>
              {data.flagged_tickets.map((t) => (
                <Card key={t.ticket_number} size="small" style={{ marginBottom: 8, borderLeft: '3px solid var(--odin-error)' }}>
                  <Text strong>{t.ticket_number} · </Text><Text strong>{t.title}</Text>{' '}
                  {t.category && <Tag color="var(--odin-accent)">{t.category}</Tag>}
                  <div><Text type="secondary">{t.reasons.join(' · ')}</Text></div>
                </Card>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default function EngineerPerformancePage() {
  const [preset, setPreset] = useState('3m');
  const [range, setRange] = useState([dayjs().subtract(3, 'month'), dayjs()]);

  const from = range?.[0]?.toISOString();
  const to = range?.[1]?.toISOString();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['engineer-performance-leaderboard', from, to],
    queryFn: () => fetchLeaderboard({ from, to }),
    enabled: Boolean(from && to),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const onPreset = (key) => {
    setPreset(key);
    const months = PRESETS.find((p) => p.key === key)?.months ?? 3;
    setRange([dayjs().subtract(months, 'month'), dayjs()]);
  };

  const team = data?.team_average;

  const exportCsv = () => {
    const csv = buildLeaderboardCsv(data?.engineers || []);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engineer-performance-${dayjs(from).format('YYYYMMDD')}-${dayjs(to).format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => [
    { title: 'Engineer', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <Space>
          <Text strong>{v}</Text>
          {r.low_sample && <Tag color="default">low sample</Tag>}
        </Space>
      ),
      sorter: (a, b) => (a.name || '').localeCompare(b.name || '') },
    { title: 'Closed', dataIndex: 'closed', key: 'closed', align: 'right',
      sorter: (a, b) => a.closed - b.closed, defaultSortOrder: 'descend' },
    { title: 'Open', dataIndex: 'open', key: 'open', align: 'right',
      sorter: (a, b) => a.open - b.open },
    { title: 'Avg res.', dataIndex: 'avg_resolution_hours', key: 'res', align: 'right',
      render: fmtHours, sorter: (a, b) => (a.avg_resolution_hours ?? 1e9) - (b.avg_resolution_hours ?? 1e9) },
    { title: '1st resp.', dataIndex: 'avg_first_response_minutes', key: 'fr', align: 'right',
      render: fmtMins, sorter: (a, b) => (a.avg_first_response_minutes ?? 1e9) - (b.avg_first_response_minutes ?? 1e9) },
    { title: 'SLA', dataIndex: 'sla_met_pct', key: 'sla', align: 'right',
      render: (v) => v == null ? '—' : <Tag color={v >= 95 ? 'green' : v >= 85 ? 'orange' : 'red'}>{fmtPct(v)}</Tag>,
      sorter: (a, b) => (a.sla_met_pct ?? -1) - (b.sla_met_pct ?? -1) },
    { title: 'CSAT', dataIndex: 'avg_satisfaction', key: 'csat', align: 'right',
      render: fmtCsat, sorter: (a, b) => (a.avg_satisfaction ?? -1) - (b.avg_satisfaction ?? -1) },
    { title: <span><WarningOutlined /> Flags</span>, dataIndex: 'flag_count', key: 'flags', align: 'right',
      render: (v) => v > 0 ? <Text type="danger" strong>{v}</Text> : v,
      sorter: (a, b) => a.flag_count - b.flag_count },
    { title: 'Top category', dataIndex: 'top_category', key: 'cat',
      render: (v) => v ? <Tag color="var(--odin-accent)">{v}</Tag> : '—' },
  ], []);

  return (
    <DashboardLayout>
      <div style={{ padding: 24 }}>
        <Title level={3}><TeamOutlined /> Engineer Performance</Title>
        <Text type="secondary">Per-engineer Autotask ticket stats. Pick a period; stats populate from ingested data.</Text>

        <Card size="small" style={{ margin: '16px 0' }}>
          <Space wrap>
            <Radio.Group value={preset} onChange={(e) => onPreset(e.target.value)} optionType="button" buttonStyle="solid">
              {PRESETS.map((p) => <Radio.Button key={p.key} value={p.key}>{p.label}</Radio.Button>)}
            </Radio.Group>
            <RangePicker
              value={range}
              onChange={(v) => { setRange(v); setPreset('custom'); }}
            />
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!data?.engineers?.length}>
              Export CSV
            </Button>
          </Space>
        </Card>

        {team && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}><Card size="small"><Statistic title="Team avg closed" value={team.avg_closed != null ? Math.round(team.avg_closed) : '—'} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small"><Statistic title="Team avg resolution" value={fmtHours(team.avg_resolution_hours)} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small"><Statistic title="Team SLA met" value={fmtPct(team.sla_met_pct)} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small"><Statistic title="Team CSAT" value={fmtCsat(team.avg_satisfaction)} /></Card></Col>
          </Row>
        )}

        {isError && (
          <Alert type="error" showIcon message="Failed to load engineer performance"
                 description={error?.message || 'Unknown error'} style={{ marginBottom: 16 }} />
        )}

        <Table
          rowKey="resource_id"
          loading={isPending}
          dataSource={data?.engineers || []}
          columns={columns}
          size="small"
          pagination={{ pageSize: 25, showSizeChanger: true }}
          locale={{ emptyText: isPending ? 'Loading…' : 'No ticket data for this period' }}
          expandable={{
            expandedRowRender: (record) => (
              <EngineerDetail resourceId={record.resource_id} from={from} to={to} team={team} />
            ),
            rowExpandable: (record) => record.closed + record.open > 0,
          }}
        />
      </div>
    </DashboardLayout>
  );
}
